import {
  Breakpoints,
  Button,
  ButtonStyles,
  ButtonWidth,
  Drawer,
  useBreakpoint,
  useSession,
  useTranslation,
} from '@nextgen-web-framework/all';
import { uniqueId } from '@nextgen-web-framework/core';
import { useEffect, useRef, useState } from 'react';
import { postPurchaseApi, transformDeliveryTrackingData, Forbidden } from '@commerce-order-webcomponents/sdk';
import { TrackDeliveryResponse } from '@commerce-order-webcomponents/sdk/services/models/TrackDeliveryResponse';

import { DeliveryOrder } from '@commerce-order-webcomponents/sdk/types/postpurchaseTypes';
import SkeletonLoader from '../common/skeleton-loader/SkeletonLoader';
import { TrackNowInputFields } from './molecules/delivery-tracking-input/types/propsTypes';
import HowToSection from './molecules/how-to-section/HowToSection';
import DeliveryPackageCard from './molecules/delivery-package-card/DeliveryPackageCard';
import DeliveryTrackingInput from './molecules/delivery-tracking-input/DeliveryTrackingInput';
import styles from './DeliveryTracking.module.scss';
import OrderNotFound from './molecules/order-not-found/OrderNotFound';
import { DeliveryTrackingPropTypes } from './types/proptypes';

const DeliveryTracking = ({ deliveryTrackingCMSData, orderId }: DeliveryTrackingPropTypes) => {
  const viewport = useBreakpoint() as Breakpoints;
  const t = useTranslation();
  const { isAnonymous } = useSession();
  const isMobileView = [Breakpoints.XS, Breakpoints.SM].includes(viewport);
  const [showDeliveryPackageResults, setShowDeliveryPackageResults] = useState(false);
  const [showOrderNotFound, setShowOrderNotFound] = useState<boolean>(false);
  const [formData, setFormData] = useState<TrackNowInputFields>({});
  const [deliveryTrackingData, setDeliveryTrackingData] = useState<DeliveryOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    offset: 1,
    limit: 10,
    totalPages: 0,
    totalRecords: 0,
  });
  const [lastOrderCard, setLastOrderCard] = useState<HTMLElement | null>();
  const orderContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (orderContainerRef?.current?.children[deliveryTrackingData.length - 1] as HTMLDivElement) {
      setLastOrderCard(orderContainerRef?.current?.children[deliveryTrackingData.length - 1] as HTMLDivElement);
    }
  }, [deliveryTrackingData.length]);
  const observer = useRef<IntersectionObserver>();

  useEffect(() => {
    if (Object.keys(formData).length > 0) {
      fetchDeliveryTrackingResults();
    }
  }, [formData]);

  useEffect(() => {
    if (window.IntersectionObserver) {
      observer.current = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) {
          const isMoreResultsAvailable = !loading && Number(pagination.offset) <= Number(pagination.totalPages);
          if (isMoreResultsAvailable) {
            fetchDeliveryTrackingResults();
          }
        }
      }, {});
    }
    return () => observer.current?.disconnect();
  }, [pagination.offset, pagination.totalPages, pagination.totalRecords, loading]);

  useEffect(() => {
    if (lastOrderCard) {
      observer.current?.disconnect();
      observer.current?.observe(lastOrderCard as HTMLElement);
    }
  }, [lastOrderCard]);

  const onTrackNowClick = (fields: TrackNowInputFields) => {
    setFormData(fields);
    setDeliveryTrackingData([]);
    setPagination((prev) => ({
      ...prev,
      totalPages: 0,
      totalRecords: 0,
      offset: 1,
    }));
  };

  const fetchDeliveryTrackingResults = () => {
    setLoading(true);
    const payload = {
      offset: pagination.offset,
      limit: pagination.limit,
      isAnonymous: isAnonymous(),
      ...formData,
    };
    postPurchaseApi
      .getDeliveryTracking({
        ...payload,
      })
      .then((res: TrackDeliveryResponse | Forbidden) => {
        if (res && 'orders' in res) {
          const data = transformDeliveryTrackingData(res);
          setShowDeliveryPackageResults(true);
          setDeliveryTrackingData([...deliveryTrackingData, ...(data?.deliveryOrders ?? [])]);
          setShowOrderNotFound(false);
          setPagination((prev) => ({
            ...prev,
            totalPages: data.totalPages,
            totalRecords: data.totalRecords,
            offset: pagination.offset + 1,
          }));
        }
        setLoading(false);
      })
      .catch(() => {
        if (deliveryTrackingData.length === 0) {
          setShowOrderNotFound(true);
          setShowDeliveryPackageResults(true);
        }
        setLoading(false);
      });
  };

  const renderDeliveryTrackingResult = () => {
    const resultCount = showOrderNotFound ? 0 : pagination.totalRecords;
    if (!loading || deliveryTrackingData.length > 0) {
      return (
        <>
          <div className={styles['delivery-tracking-details-container']} data-testid="delivery-tracking-results-testid">
            <div className={styles['delivery-tracking-results']}>
              {t('order.deliveryTracking.results').replace('{{count}}', String(resultCount ?? 0))}
            </div>
            {deliveryTrackingData && deliveryTrackingData.length > 0 && !showOrderNotFound ? (
              <div className={styles['delivery-tracking-package-list']} ref={orderContainerRef}>
                {deliveryTrackingData.map((order, index) => (
                  <div key={`${order.orderId}_${index.toString()}`}>
                    <DeliveryPackageCard
                      key={uniqueId()}
                      orderId={order.orderId}
                      packageLable={order.packageLabel}
                      orderTrackingNote={order.orderTrackingNote}
                      deliveryProgressStages={order.deliveryProgressStages}
                      carrierPartnerLogoUrl={order.carrierImageUrl}
                      productImageUrls={order.productImageUrls}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <OrderNotFound />
            )}
          </div>
          {loading && (
            <div className={styles['skeleton-loader-container']}>
              <SkeletonLoader />
            </div>
          )}
        </>
      );
    }
    return <></>;
  };
  return (
    <div className={styles['delivery-tracking-container']} data-testid="delivery-tracking-container-testid">
      <DeliveryTrackingInput
        loading={loading}
        onTrackNowClick={(fields) => onTrackNowClick(fields)}
        orderId={orderId}
      />
      {showDeliveryPackageResults && (
        <>
          {isMobileView ? (
            renderDeliveryTrackingResult()
          ) : (
            <Drawer
              footer={
                <Button
                  buttonStyle={ButtonStyles.SOLID}
                  onClick={() => setShowDeliveryPackageResults(false)}
                  title={t('order.orderTracking.confirmCtaLabel')}
                  buttonWidth={ButtonWidth.FULL_WIDTH}
                />
              }
              header={<>{t('order.deliveryTracking.deliveryDetails')}</>}
              closeIcon={true}
              onClose={() => setShowDeliveryPackageResults(false)}
              bottomGap={0}
            >
              {renderDeliveryTrackingResult()}
            </Drawer>
          )}
        </>
      )}
      {deliveryTrackingCMSData?.heading && (
        <HowToSection title={deliveryTrackingCMSData?.heading} howToList={deliveryTrackingCMSData?.faq?.faq} />
      )}
    </div>
  );
};

export default DeliveryTracking;
